import { useState, useEffect, useCallback, useRef } from "react";
import isEqual from "lodash.isequal";
import {apiFetch, ApiResult} from "../util";

// Deep compare hook for stable dependencies
function useDeepCompareMemoize<T>(value: T): T {
    const ref = useRef<T>(value);
    if (!isEqual(value, ref.current)) ref.current = value;
    return ref.current;
}

export function useApiFetch<T>(
    url: string | null,
    options?: RequestInit,
    dependencies: any[] = [],
    toastError = true
) {
    const [fetchResult, setFetchResult] = useState<T | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean | null>(null);
    const [message, setMessage] = useState<string | undefined>(undefined);

    const memoOptions = useDeepCompareMemoize(options);
    const abortRef = useRef<AbortController | null>(null);

    const fetchData = useCallback(async (): Promise<ApiResult<T>> => {
        if (!url) return { error: "No URL", success: false, body: null as any };

        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        const { signal } = controller;

        setLoading(true);
        setError(null);
        setSuccess(null);
        setMessage(undefined);

        try {
            const result = await apiFetch<T>(
                url,
                { ...memoOptions, signal } as RequestInit,
                toastError
            );

            if (!signal.aborted) {
                setFetchResult(result.body);
                setSuccess(result.success);
                setMessage(result.message);
                if (!result.success) setError(result.error);
            }

            return result;
        } catch (err: any) {
            if (!signal.aborted) setError(err.message || err.toString());
            return { error: err.toString(), success: false, body: null as any };
        } finally {
            if (!signal.aborted) setLoading(false);
        }
    }, [url, memoOptions, toastError]);

    useEffect(() => {
        fetchData().then();

        return () => {
            if (abortRef.current) abortRef.current.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies.length ? dependencies : [fetchData]);

    return { fetchResult, loading, error, success, message, refetch: fetchData };
}
